<script lang="ts">
  import Table from "../../Table.svelte";
  import Button from "../../Button.svelte";
  import Pagination from "../../Pagination.svelte";
  import SearchInput from "../../SearchInput.svelte";
  import { onMount } from "svelte";
  import { push } from "svelte-spa-router";
  import {
    PayoutService,
    type DonationPayout,
    type PaginationInfo,
  } from "../../../lib/api/charityServices";

  /** received = Donations Received; pending = Pending Confirmation */
  export let mode: 'received' | 'pending' = 'received';

  const COMPLETED_STATUS_UUID = "3d8b53f5-11e2-4f8b-9d3b-bb86dcd1e641";

  let search = "";
  let loading = false;
  let error: string | null = null;
  let currentPage = 1;
  let paging: PaginationInfo | null = null;
  let payouts: DonationPayout[] = [];

  async function fetchPayouts() {
    loading = true;
    error = null;
    try {
      const result = await PayoutService.getPayouts({
        status: COMPLETED_STATUS_UUID,
        received: mode === 'received',
        search,
        page: currentPage,
        per_page: 20,
      });
      if (result.success && result.payouts) {
        payouts = result.payouts.filter(p => {
          const status = p.donation_payout_status?.name || p.status?.name || '';
          return status.toLowerCase() === 'completed';
        });
        payouts.sort((a, b) => {
          const dateA = new Date(a.transaction_date || 0).getTime();
          const dateB = new Date(b.transaction_date || 0).getTime();
          return dateB - dateA;
        });
        paging = result.paging || null;
      } else {
        error = result.error || "Failed to load payouts";
        payouts = [];
      }
    } catch (err) {
      console.error("Error fetching completed payouts:", err);
      error = "An error occurred while fetching payouts";
      payouts = [];
    } finally {
      loading = false;
    }
  }

  function formatDate(dateString: string | undefined): string {
    if (!dateString) return "";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  }

  function formatCurrency(amount: number | undefined): string {
    if (amount === undefined) return "";
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);
  }

  // Confirmation status display (same names and styling as payout detail view)
  function getDisplayStatus(payout: DonationPayout & { received?: boolean; charity_marked_received_at?: string | null }): string {
    const status = (payout.donation_payout_status?.name || payout.status?.name || "").toUpperCase();
    if (status === "COMPLETED") {
      if (payout.received || payout.charity_marked_received_at) return "Confirmed Received";
      return "Pending Receival Confirmation";
    }
    return payout.donation_payout_status?.name || payout.status?.name || "Pending";
  }

  function getStatusClass(displayStatus: string): string {
    switch (displayStatus) {
      case "Confirmed Received": return "received";
      case "Pending Receival Confirmation": return "pending-receival";
      default: break;
    }
    switch (displayStatus.toUpperCase()) {
      case "COMPLETED":
      case "APPROVED": return "approved";
      case "FAILED":
      case "REJECTED": return "rejected";
      case "PENDING":
      default: return "pending";
    }
  }

  function getStatusIcon(displayStatus: string): string {
    switch (displayStatus) {
      case "Confirmed Received":
        return "<svg viewBox=\"0 0 24 24\" width=\"16\" height=\"16\" fill=\"currentColor\"><path d=\"M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z\"/></svg>";
      case "Pending Receival Confirmation":
        return "<svg viewBox=\"0 0 24 24\" width=\"16\" height=\"16\" fill=\"currentColor\"><path d=\"M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z\"/><path d=\"M12.5 7H11v6l5.25 3.15.75-1.23-4.5-2.67z\"/></svg>";
      default: break;
    }
    switch (displayStatus.toUpperCase()) {
      case "COMPLETED":
      case "APPROVED":
        return "<svg viewBox=\"0 0 24 24\" width=\"16\" height=\"16\" fill=\"currentColor\"><path d=\"M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z\"/></svg>";
      case "FAILED":
      case "REJECTED":
        return "<svg viewBox=\"0 0 24 24\" width=\"16\" height=\"16\" fill=\"currentColor\"><path d=\"M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z\"/></svg>";
      case "PENDING":
      default:
        return "<svg viewBox=\"0 0 24 24\" width=\"16\" height=\"16\" fill=\"currentColor\"><path d=\"M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z\"/><path d=\"M12.5 7H11v6l5.25 3.15.75-1.23-4.5-2.67z\"/></svg>";
    }
  }

  const columns = [
    {
      key: "employer",
      label: "Employer",
      width: "22%",
      render: (_: unknown, row: any) => {
        const name = row.employer_name || row.company || "—";
        const uuid = row.employer_uuid;
        if (uuid) {
          return `<button type="button" class="employer-link" data-employer-uuid="${uuid}">${name}</button>`;
        }
        return `<span class="col-employer">${name}</span>`;
      },
    },
    {
      key: "date",
      label: "Date",
      width: "16%",
      render: (_: unknown, row: any) => `<span class="col-date">${row.date}</span>`,
    },
    {
      key: "gross_amount",
      label: "Gross Amount",
      width: "14%",
      render: (_: unknown, row: any) => `<span class="col-amount">${formatCurrency(row.gross_amount)}</span>`,
    },
    {
      key: "fee",
      label: "Fee",
      width: "14%",
      render: (_: unknown, row: any) => `<span class="col-fee">${row.fee_display}</span>`,
    },
    {
      key: "net_payment",
      label: "Net to Charity",
      width: "14%",
      render: (_: unknown, row: any) => `<span class="col-net">${formatCurrency(row.net_payment)}</span>`,
    },
    {
      key: "confirmation_status",
      label: "Confirmation Status",
      width: "20%",
      align: "center" as const,
      render: (_: unknown, row: any) => {
        const displayStatus = row.displayStatus || "Pending Receival Confirmation";
        const statusClass = getStatusClass(displayStatus);
        const icon = getStatusIcon(displayStatus);
        return `<div class="status-badge status-${statusClass}">${icon}<span>${displayStatus}</span></div>`;
      },
    },
  ];

  function transformPayoutData(list: DonationPayout[]) {
    return list.map((p) => {
      const row = p as DonationPayout & { received?: boolean; charity_marked_received_at?: string | null };
      const received = row.received || !!row.charity_marked_received_at;
      const grossAmount = p.payment_received || parseFloat(p.total_amount || p.amount || "0");
      const feePercentage = p.l2g_fee_percentage || 0;
      const feeAmount = p.l2g_fee_amount || 0;
      const feeDisplay = feePercentage > 0 
        ? `${feePercentage}% (${formatCurrency(feeAmount)})`
        : '—';
      return {
        uuid: p.uuid,
        date: formatDate(p.transaction_date) || "—",
        company: p.company || "—",
        employer_name: p.employer?.name || p.company || "—",
        employer_uuid: p.employer?.uuid ?? null,
        gross_amount: grossAmount,
        fee_display: feeDisplay,
        net_payment: p.net_payment,
        received,
        displayStatus: getDisplayStatus({ ...p, received, charity_marked_received_at: row.charity_marked_received_at }),
      };
    });
  }

  // Only employer-name clicks go to view-employer; row click goes to payout detail. Use capture so we handle link first and stop row click.
  function handleContainerClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const link = target.closest(".employer-link") as HTMLElement;
    if (link) {
      event.preventDefault();
      event.stopPropagation();
      const uuid = link.dataset.employerUuid;
      if (uuid) push(`/charity/view-employer/${uuid}`);
    }
  }

  function handleRowClick(row: { uuid?: string }) {
    if (row?.uuid) push(`/payout-detail/${row.uuid}`);
  }

  function handlePageChange(event: CustomEvent<{ page: number }>) {
    currentPage = event.detail.page;
    fetchPayouts();
  }

  let searchTimeout: ReturnType<typeof setTimeout>;
  function onSearch() {
    currentPage = 1;
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => fetchPayouts(), 300);
  }

  function resetFilters() {
    search = "";
    currentPage = 1;
    fetchPayouts();
  }

  onMount(() => { fetchPayouts(); });
</script>

<div class="payouts-container">
  <!-- Header with filters (same layout as incoming donations) -->
  <div class="page-header">
    <div class="filters-section">
      <div class="filter-item search-item">
        <SearchInput
          bind:value={search}
          placeholder="Search"
          on:input={onSearch}
          width="100%"
        />
      </div>
    </div>
    <div class="search-section">
      <Button variant="secondary" on:click={resetFilters}>Reset</Button>
    </div>
  </div>

  {#if loading && payouts.length === 0}
    <div class="loading-container">
      <div class="loading-spinner"></div>
      <p>Loading…</p>
    </div>
  {:else if error}
    <div class="error-container">
      <p class="error-message">{error}</p>
      <Button variant="primary" on:click={fetchPayouts}>Try again</Button>
    </div>
  {:else if payouts.length === 0}
    <div class="empty-state">
      <svg viewBox="0 0 24 24" width="48" height="48" fill="#9ca3af">
        <path d="M19 5v14H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z" />
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
      </svg>
      <p>{mode === 'pending' ? 'No payouts awaiting confirmation' : 'No donations received yet'}</p>
      <Button variant="primary" on:click={resetFilters}>Clear filters</Button>
    </div>
  {:else}
    <div class="table-wrap" on:click|capture={handleContainerClick} role="presentation">
      {#if loading}
        <div class="loading-overlay">
          <div class="loading-spinner"></div>
        </div>
      {/if}
      <Table
        columns={columns}
        data={transformPayoutData(payouts)}
        onRowClick={handleRowClick}
      />
      {#if paging && paging.last_page > 1}
        <div class="pagination-wrapper">
          <Pagination
            currentPage={currentPage}
            totalPages={paging.last_page}
            maxVisiblePages={7}
            on:pageChange={handlePageChange}
          />
        </div>
      {/if}
    </div>
  {/if}
</div>

<style>
  .payouts-container {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    padding: 1.5rem;
    background-color: #f7fafc;
    min-height: calc(100vh - 70px);
  }

  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 2rem;
    background: white;
    padding: 1.5rem;
    border-radius: 12px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
    width: 100%;
  }

  .filters-section {
    display: flex;
    gap: 1rem;
    align-items: center;
    flex: 1;
    flex-wrap: wrap;
  }

  .filter-item {
    flex: 0 0 auto;
    min-width: 180px;
    max-width: 200px;
  }

  .filter-item.search-item {
    min-width: 200px;
    max-width: 250px;
    flex: 1;
  }

  .search-section {
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }

  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    text-align: center;
  }

  .loading-spinner {
    width: 2rem;
    height: 2rem;
    border: 3px solid #f3f4f6;
    border-top: 3px solid var(--color-primary, #f0b429);
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    text-align: center;
  }

  .error-message {
    color: #dc2626;
    font-size: 1.1rem;
    margin-bottom: 1rem;
  }

  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4rem 2rem;
    text-align: center;
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  }

  .empty-state p {
    color: #6b7280;
    font-size: 1.1rem;
    margin: 1rem 0 1.5rem;
  }

  .table-wrap {
    position: relative;
    min-width: 0;
  }

  .loading-overlay {
    position: absolute;
    inset: 0;
    background: rgba(255, 255, 255, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 5;
  }

  .pagination-wrapper {
    margin-top: 1.5rem;
    padding: 0 1rem;
  }

  :global(.col-date) { color: #6b7280; }
  :global(.col-employer) { font-weight: 500; color: #111827; }
  :global(.employer-link) {
    background: none;
    border: none;
    padding: 0;
    font: inherit;
    font-weight: 500;
    color: var(--color-primary, #f0b429);
    cursor: pointer;
    text-align: left;
    text-decoration: none;
    transition: color 0.2s;
  }
  :global(.employer-link:hover) {
    color: var(--color-primary-hover, #d69e20);
    text-decoration: underline;
  }
  :global(.employer-link:focus) {
    outline: none;
  }
  :global(.col-amount) { font-weight: 500; color: #6b7280; }
  :global(.col-fee) { font-size: 0.875rem; color: #6b7280; }
  :global(.col-net) { font-weight: 600; color: #059669; }

  /* Confirmation status badges (same as payout detail view) */
  :global(.status-badge) {
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.875rem;
    font-weight: 500;
    gap: 0.375rem;
  }

  :global(.status-badge svg) {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  :global(.status-badge span) {
    display: block;
  }

  :global(.status-received) {
    background-color: #d1fae5;
    color: #065f46;
  }

  :global(.status-pending-receival) {
    background-color: #dbeafe;
    color: #1e40af;
  }

  @media (max-width: 1280px) {
    .page-header {
      flex-wrap: wrap;
      gap: 1rem;
    }
    .filters-section {
      flex: 1 1 100%;
      justify-content: flex-start;
      margin-bottom: 0.5rem;
    }
    .search-section {
      flex: 0 0 auto;
      width: auto;
      margin-left: auto;
    }
  }

  @media (max-width: 1024px) {
    .page-header {
      flex-direction: column;
      align-items: stretch;
    }
    .filters-section {
      flex-wrap: wrap;
      gap: 0.75rem;
    }
    .filter-item {
      flex: 1 1 calc(33% - 0.75rem);
      min-width: 180px;
      max-width: none;
    }
    .search-section {
      width: 100%;
      justify-content: flex-end;
    }
  }

  @media (max-width: 768px) {
    .payouts-container {
      padding: 1rem;
    }
    .page-header {
      padding: 1rem;
    }
    .filters-section {
      flex-direction: column;
      gap: 0.75rem;
    }
    .filter-item {
      width: 100%;
      min-width: 100%;
      max-width: none;
    }
    .search-section {
      width: 100%;
    }
    .search-section :global(button) {
      width: 100%;
    }
    :global(.dropdown-container) {
      width: 100% !important;
    }
  }
</style>
